import { Box, Link, SxProps, Theme, useMediaQuery, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';

type MarkdownOutputProps = Readonly<{
  text: string;
}>;

export const MarkdownOutput = ({ text }: MarkdownOutputProps) => {
  const isMobile = useMediaQuery('(max-width: 600px)');

  if (!text) return null;

  // Filter out [some string](careerpath:ID) patterns and remove parentheses from career path names
  const filteredContent = text.replace(/\[([^\]]+)\]\(careerpath:\d+\)/g, '$1');

  // replace double spaces with a single space for proper markdown formatting
  const cleanedMarkdown = filteredContent.replace(/ {2}\n/g, '\n');

  return (
    <Box sx={containerSx}>
      <ReactMarkdown
        components={{
          a: ({ href, children }) => (
            <Link href={href} target="_blank" rel="noopener noreferrer" sx={linkStyle}>
              {children}
            </Link>
          ),
          p: ({ children }) => (
            <Typography variant="text-md-regular" sx={paragraphStyle}>
              {children}
            </Typography>
          ),
          h1: ({ children }) => (
            <Typography variant="text-lg-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="text-md-semibold" sx={headingStyle}>
              {children}
            </Typography>
          ),
          ul: ({ children }) => (
            <Typography component="ul" variant="text-md-regular" sx={listStyle(isMobile)}>
              {children}
            </Typography>
          ),
          ol: ({ children }) => (
            <Typography component="ol" variant="text-md-regular" sx={listStyle(isMobile)}>
              {children}
            </Typography>
          ),
          li: ({ children }) => (
            <Typography component="li" variant="text-md-regular" sx={listItemStyle}>
              {children}
            </Typography>
          ),
          strong: ({ children }) => (
            <Typography variant="text-md-semibold" component="span">
              {children}
            </Typography>
          ),
          em: ({ children }) => (
            <Typography variant="text-md-regular" component="span" sx={emStyle}>
              {children}
            </Typography>
          ),
        }}
      >
        {cleanedMarkdown}
      </ReactMarkdown>
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  maxWidth: 688,
  mx: 'auto',
  py: backrs.spacing['spacing-md'],
});

const listStyle =
  (isMobile: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    pl: isMobile ? backrs.spacing['spacing-md'] : backrs.spacing['spacing-xl'],
    mt: isMobile ? backrs.spacing['spacing-md'] : backrs.spacing['spacing-lg'],
    mb: isMobile ? backrs.spacing['spacing-md'] : backrs.spacing['spacing-lg'],
  });

const listItemStyle: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-xs'],
});

const emStyle: SxProps<Theme> = {
  fontStyle: 'italic',
};

const linkStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Blue/500'],
  fontWeight: 'bold',
});

const paragraphStyle: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-none'],
});

const headingStyle: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-lg'],
  mb: backrs.spacing['spacing-md'],
});
